/**
 * Shared Icon Components
 *
 * Reusable SVG icons used across the application.
 * Based on Lucide icon style (24x24, stroke-based).
 */

interface IconProps {
  className?: string;
}

export function LoadingSpinner({ className }: IconProps) {
  return (
    <svg
      className={`animate-spin ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      role="img"
      aria-label="Loading"
      data-testid="loading-spinner"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export function CheckIcon({ className }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function ClockIcon({ className }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export function XIcon({ className }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export { MailIcon } from "./MailIcon";
export { MailCheckIcon } from "./MailCheckIcon";
export { ShieldCheckIcon } from "./ShieldCheckIcon";
export { DoubleCheckIcon } from "./DoubleCheckIcon";

export function CameraIcon({ className }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );
}

export function LockIcon({ className }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role="img"
      aria-label="Secure"
    >
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export function ApplePayIcon({ className }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 165.52 105.97"
      fill="currentColor"
      className={className}
      role="img"
      aria-label="Apple Pay"
    >
      {/* Apple logo + "Pay" text */}
      <path d="M41.64 34.36c-2.72 3.2-7.07 5.68-11.38 5.32-.54-4.35 1.58-8.97 4.06-11.84C37.05 24.6 41.82 22.28 45.62 22c.46 4.52-1.31 8.97-3.98 12.36zm3.93 6.27c-6.28-.37-11.62 3.57-14.6 3.57-3.02 0-7.6-3.37-12.54-3.29-6.44.09-12.41 3.74-15.72 9.55-6.73 11.64-1.72 28.89 4.77 38.35 3.2 4.64 6.98 9.84 11.99 9.67 4.77-.21 6.61-3.1 12.42-3.1 5.76 0 7.48 3.1 12.54 2.97 5.18-.09 8.51-4.64 11.67-9.3 3.65-5.35 5.14-10.53 5.22-10.82-.12-.04-10.07-3.86-10.16-15.34-.08-9.59 7.81-14.19 8.18-14.39-4.52-6.61-11.46-7.35-13.88-7.56l.11.39zM86.95 28.18v67.54h10.42V65.56h14.44c13.18 0 22.44-9.05 22.44-22.72 0-13.67-9.09-22.66-22.15-22.66H86.95zm10.42 8.71h12.02c9.05 0 14.21 4.86 14.21 13.38 0 8.52-5.16 13.42-14.25 13.42H97.37V36.89zm56.42 59.64c6.53 0 12.58-3.32 15.33-8.59h.21v8.09h9.63V63.48c0-9.67-7.73-15.89-19.61-15.89-11.04 0-19.15 6.35-19.44 15.07h9.34c.79-4.14 4.73-6.86 9.63-6.86 6.22 0 9.72 2.91 9.72 8.26v3.61l-12.71.75c-11.84.67-18.24 5.56-18.24 13.97 0 8.51 6.61 14.27 16.14 14.27v-.13zm2.78-7.81c-5.43 0-8.88-2.61-8.88-6.61 0-4.14 3.33-6.53 9.67-6.9l11.33-.71v3.69c0 6.28-5.31 10.74-12.12 10.53z" />
    </svg>
  );
}

export function GooglePayIcon({ className }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 435.97 173.13"
      className={className}
      role="img"
      aria-label="Google Pay"
    >
      {/* Multicolor Google "G" mark + "Pay" text */}
      <path fill="#5F6368" d="M206.2 84.58v50.75h-16.1V0h42.68c10.27-.2 20.17 3.73 27.6 10.96 7.63 6.84 11.94 16.71 11.74 27.06.2 10.45-4.11 20.42-11.74 27.46-7.53 7.33-16.91 10.96-28.21 10.96H206.2v8.14zm0-68.48v44.38h27.07c6.04.2 11.88-2.24 16.1-6.64 8.54-8.34 8.74-22.06.59-30.6l-.59-.59c-4.21-4.61-10.16-7.04-16.1-6.84H206.2v.29zm114.77 27.27c11.88 0 21.25 3.24 28.11 9.62 6.84 6.44 10.26 15.25 10.26 26.59v53.79h-15.42v-12.12h-.69c-6.64 9.82-15.52 14.73-26.5 14.73-9.38 0-17.22-2.85-23.48-8.44-6.04-5.2-9.57-12.83-9.38-20.81 0-8.83 3.34-15.86 10.07-20.96 6.64-5.1 15.52-7.63 26.6-7.63 9.47 0 17.22 1.75 23.27 5.2v-3.63c.1-6.25-2.75-12.22-7.73-16.12-4.99-4.21-11.23-6.55-17.72-6.44-10.26 0-18.39 4.31-24.35 13.04l-14.24-8.93c8.64-12.83 21.48-19.27 38.6-19.27v-.62zm-20.55 67.93c3.14 4.8 8.33 7.83 14.04 8.14 6.04 0 11.88-2.44 16.1-6.84 4.89-4.41 7.63-10.46 7.63-16.91-4.89-3.93-11.74-5.88-20.47-5.88-6.34 0-11.63 1.55-15.82 4.7-4.31 3.14-6.44 7.04-6.44 11.54.1 2.03.59 3.93 1.55 5.68l3.41-.43zm119.58-64.58L371.83 173.1h-16.49l17.42-37.79-30.84-68.48H358.9l21.46 51.63h.29l20.86-51.63H418v.49z" />
      <path fill="#4285F4" d="M144.78 72.27c0-5.1-.39-10.17-1.26-15.18H73.89v28.72h39.73c-1.55 8.93-6.84 16.91-14.67 21.81v17.82h23.56c13.86-12.81 21.84-31.72 22.27-53.17z" />
      <path fill="#34A853" d="M73.89 143.07c19.88 0 36.68-6.55 48.87-17.82l-23.56-17.82c-6.64 4.61-15.23 7.14-25.31 7.14-19.29 0-35.7-13.04-41.64-30.64H8.01v18.41c12.59 24.97 38.11 40.84 65.88 40.73z" />
      <path fill="#FBBC04" d="M32.25 83.93c-3.34-9.72-3.34-20.32 0-30.04V35.48H8.01C-2.76 55.1-2.76 78.2 8.01 97.83l24.24-13.9z" />
      <path fill="#EA4335" d="M73.89 23.25c10.76-.2 21.12 3.83 28.98 11.24l21.45-21.46C111.04 1.05 92.88-4.02 73.89.05 46.12-.06 20.6 15.82 8.01 40.78l24.24 18.41c5.94-17.6 22.35-30.64 41.64-35.94z" />
    </svg>
  );
}

export function AlertTriangleIcon({ className }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
